import { useState, FormEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { UserPlus, Mail, User, Lock, AlertCircle, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { publicAPI } from '../services/api'

// Blocklist of generic email providers - only corporate emails allowed
const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.no',
  'outlook.com',
  'outlook.no',
  'live.com',
  'live.no',
  'yahoo.com',
  'yahoo.no',
  'aol.com',
  'icloud.com',
  'me.com',
  'protonmail.com',
  'proton.me',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'yandex.com',
  'zoho.com',
  'tutanota.com',
  'fastmail.com',
  'mail.ru',
  'inbox.com',
  'ymail.com'
]

interface GACContent {
  id: number
  Title: string
  Content: string
  ContentType: string
  IsActive: number
  ModifiedTime: string
}

export default function Register() {
  const { t } = useTranslation('auth')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [gacAnnouncementId, setGacAnnouncementId] = useState<number | null>(null)

  const [success, setSuccess] = useState(false)

  const { register, logout } = useAuth()

  // Fetch active GAC announcement ID in background
  useEffect(() => {
    fetchGACAnnouncementId()
  }, [])

  const fetchGACAnnouncementId = async () => {
    try {
      const response = await publicAPI.getAnnouncements({ 
        content_type: 'generalterms'
      })
      
      // Filter for active announcement (IsActive = 1)
      const activeAnnouncement = response.data.data?.find((ann: GACContent) => ann.IsActive === 1)
      
      if (activeAnnouncement) {
        setGacAnnouncementId(activeAnnouncement.id)
      }
    } catch (err) {
      console.error('Failed to fetch GAC announcement:', err)
      // Don't show error to user - registration will work without announcement_id for backwards compatibility
    }
  }

  // Password strength calculation
  const checkPasswordStrength = (pass: string): number => {
    let strength = 0
    if (pass.length >= 8) strength++
    if (pass.length >= 12) strength++
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength++
    if (/\d/.test(pass)) strength++
    if (/[^a-zA-Z0-9]/.test(pass)) strength++
    return strength
  }

  const passwordStrength = checkPasswordStrength(password)
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-red-500'
    if (passwordStrength <= 2) return 'bg-yellow-500'
    if (passwordStrength <= 3) return 'bg-blue-500'
    return 'bg-green-500'
  }

  const getStrengthText = () => {
    if (passwordStrength <= 1) return t('register.strength.weak')
    if (passwordStrength <= 2) return t('register.strength.medium')
    if (passwordStrength <= 3) return t('register.strength.good')
    return t('register.strength.strong')
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    // Extract email domain and validate
    const emailDomain = email.split('@')[1]?.toLowerCase()
    
    if (!emailDomain) {
      setError(t('register.errors.invalidEmail'))
      return
    }

    if (BLOCKED_EMAIL_DOMAINS.includes(emailDomain)) {
      setError(t('register.errors.corporateOnly'))
      return
    }

    // Validation
    if (password.length < 8) {
      setError(t('register.errors.passwordTooShort'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('register.errors.passwordMismatch'))
      return
    }

    if (!acceptedTerms) {
      setError(t('register.errors.mustAcceptTerms'))
      return
    }

    setLoading(true)

    try {
      // Send announcement_id if available, undefined otherwise (backend will handle)
      const result = await register(email, password, username || undefined, gacAnnouncementId || undefined)
      
      if (result.success) {
        // Logout since account needs admin approval before access
        await logout()
        setSuccess(true)
      } else {
        setError(result.error || t('register.errors.failed'))
      }
    } catch (err: any) {
      setError(err.message || t('register.errors.unexpected'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-green-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('register.success.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('register.success.received')}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('register.success.emailWhenApproved')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              {t('backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-green-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <UserPlus className="h-10 w-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            {t('register.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('register.haveAccount')}{' '}
            <Link to="/login" className="font-medium text-green-600 hover:text-green-500 dark:text-green-400">
              {t('register.loginLink')}
            </Link>
          </p>
        </div>

        {/* Registration Form */}
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-8 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Mail className="h-4 w-4 inline mr-1" />
                {t('emailLabelRequired')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('emailPlaceholder')}
              />
            </div>

            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                {t('register.usernameLabel')}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder={t('register.usernamePlaceholder')}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Lock className="h-4 w-4 inline mr-1" />
                {t('register.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('register.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-400">{t('register.strengthLabel')}</span>
                    <span className={`font-medium ${
                      passwordStrength <= 1 ? 'text-red-600' : 
                      passwordStrength <= 2 ? 'text-yellow-600' : 
                      passwordStrength <= 3 ? 'text-blue-600' : 
                      'text-green-600'
                    }`}>
                      {getStrengthText()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getStrengthColor()}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Lock className="h-4 w-4 inline mr-1" />
                {t('register.confirmLabel')}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none rounded-lg relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder={t('register.confirmPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className="mt-2 flex items-center text-sm">
                  {passwordsMatch ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-green-600 dark:text-green-400">{t('register.passwordsMatch')}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                      <span className="text-red-600 dark:text-red-400">{t('register.passwordsDontMatch')}</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded-sm"
              />
              <label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300">
                {t('register.acceptTermsPrefix')}{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 underline"
                >
                  {t('register.termsLink')}
                </Link>
              </label>
            </div>

            {/* Admin approval info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                {t('register.approvalInfo')}
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !passwordsMatch || !acceptedTerms}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('register.submitting')}
                </span>
              ) : (
                t('register.submit')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
